import * as Yup from 'yup';

export type ValidationField = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  email?: boolean;
  phone?: boolean;
  custom?: (value: unknown, allValues?: unknown) => string | undefined;
};

export type ValidationConfig = Record<string, ValidationField>;

/**
 * Builds a Yup object schema from per-field validation config.
 * Messages are i18n keys (form.required, form.invalid_email) —
 * FormControls call t() when rendering errors.
 */
export function createValidationSchema(config: ValidationConfig) {
  const schemaFields: Record<string, Yup.AnySchema> = {};

  Object.keys(config).forEach((fieldName) => {
    const fieldConfig = config[fieldName];
    let fieldSchema = Yup.string();

    if (fieldConfig.email) {
      fieldSchema = fieldSchema.email('form.invalid_email');
    }

    if (fieldConfig.phone) {
      fieldSchema = fieldSchema.matches(
        /^[\+]?[1-9][\d]{0,15}$/,
        'form.required',
      );
    }

    if (fieldConfig.minLength) {
      fieldSchema = fieldSchema.min(fieldConfig.minLength, 'form.required');
    }

    if (fieldConfig.maxLength) {
      fieldSchema = fieldSchema.max(fieldConfig.maxLength, 'form.required');
    }

    if (fieldConfig.custom) {
      fieldSchema = fieldSchema.test('custom', '', function (value) {
        const result = fieldConfig.custom!(value, this.parent);
        if (result) {
          return this.createError({ message: result });
        }
        return true;
      });
    }

    if (fieldConfig.required) {
      fieldSchema = fieldSchema.required('form.required');
    } else {
      fieldSchema = fieldSchema.nullable();
    }

    schemaFields[fieldName] = fieldSchema;
  });

  return Yup.object().shape(schemaFields);
}

export function isFieldRequired(
  schema: Yup.ObjectSchema<any>,
  fieldName: string,
): boolean {
  try {
    schema.validateSync({ [fieldName]: '' }, { abortEarly: false });
    return false;
  } catch (error: any) {
    if (error?.inner) {
      return error.inner.some(
        (err: { path?: string; type?: string }) =>
          err.path === fieldName &&
          (err.type === 'required' || err.type === 'nullable'),
      );
    }
    return error?.path === fieldName;
  }
}
